import type { ProviderConfig } from "@/api/providers";

export type ProviderTypeFilter = "all" | "ats" | "board" | "local-parser";
export type ProviderStatusFilter = "all" | "enabled" | "disabled";

export const TYPE_LABELS: Record<string, string> = {
  ats: "ATS",
  board: "Board",
  "local-parser": "Custom",
};

export const TYPE_COLORS: Record<string, string> = {
  ats: "bg-sky-500/10 text-sky-400",
  board: "bg-emerald-500/10 text-emerald-400",
  "local-parser": "bg-violet-500/10 text-violet-400",
};

export const FEATURE_LABELS: Record<string, string> = {
  detect: "Auto-detect",
  "api-url": "API URL override",
  pagination: "Pagination",
  salary: "Salary data",
  description: "Full descriptions",
  "posted-at": "Posted date",
  "multi-location": "Multi-location",
  scanner: "Live scanner",
};

export const TYPE_FILTER_TABS: ProviderTypeFilter[] = [
  "all",
  "ats",
  "board",
  "local-parser",
];

export const STATUS_FILTER_TABS: ProviderStatusFilter[] = [
  "all",
  "enabled",
  "disabled",
];

export function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? feature;
}

export function countEnabledProviders(providers: ProviderConfig[]): number {
  return providers.filter((p) => p.enabled).length;
}

export interface ProviderFilterOptions {
  search?: string;
  type?: ProviderTypeFilter;
  status?: ProviderStatusFilter;
}

export function filterProviders(
  providers: ProviderConfig[],
  { search = "", type = "all", status = "all" }: ProviderFilterOptions,
): ProviderConfig[] {
  const q = search.trim().toLowerCase();

  return providers.filter((p) => {
    if (type !== "all" && p.type !== type) return false;
    if (status === "enabled" && !p.enabled) return false;
    if (status === "disabled" && p.enabled) return false;
    if (q) {
      return (
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.providerId.toLowerCase().includes(q)
      );
    }
    return true;
  });
}

export function typeHelpText(type: ProviderConfig["type"]): string {
  switch (type) {
    case "board":
      return "Board aggregators are included in your Free Scan when you select them on Portals → Providers.";
    case "ats":
      return "Scannable ATS providers appear in Explore → Company Scan when a tracked company uses that ATS and has a board token in the directory. Scannability is configured in the app provider catalog.";
    case "local-parser":
      return "Custom parsers run local scripts configured in your CLI settings to extract jobs from careers pages.";
  }
}

export function hasIntegrationDetails(provider: ProviderConfig): boolean {
  return !!(
    provider.urlPattern ||
    (provider.allowedHosts && provider.allowedHosts.length > 0) ||
    provider.supportsDetect !== undefined ||
    provider.supportsApiUrl !== undefined ||
    provider.timeoutMs !== undefined ||
    provider.retryPolicy ||
    provider.scannerAdapter
  );
}

export function hasConfigData(config: Record<string, unknown> | undefined): boolean {
  return !!config && Object.keys(config).length > 0;
}

export type PortalSelectionFilter = "all" | "selected" | "unselected";

export const PORTAL_SELECTION_FILTER_TABS: PortalSelectionFilter[] = [
  "all",
  "selected",
  "unselected",
];

export function filterPortalProviders(
  providers: ProviderConfig[],
  selectedBoardIds: Set<string>,
  {
    search = "",
    type = "board",
    selection = "all",
  }: {
    search?: string;
    type?: ProviderTypeFilter;
    selection?: PortalSelectionFilter;
  },
): ProviderConfig[] {
  return filterProviders(providers, { search, type, status: "all" }).filter((p) => {
    const isSelected = selectedBoardIds.has(p.providerId);
    if (selection === "selected" && !isSelected) return false;
    if (selection === "unselected" && isSelected) return false;
    return true;
  });
}

export function isScannableProvider(provider: ProviderConfig): boolean {
  return provider.scannable === true;
}

export function isScannableBoardProvider(provider: ProviderConfig): boolean {
  return provider.type === "board" && isScannableProvider(provider);
}

export function isScannableAtsProvider(provider: ProviderConfig): boolean {
  return provider.type === "ats" && isScannableProvider(provider);
}

export function scannableAtsProviderIds(providers: ProviderConfig[]): string[] {
  return providers
    .filter(isScannableAtsProvider)
    .map((p) => p.providerId)
    .sort();
}

export function formatProviderDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
